import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  WhatsappLogo, EnvelopeSimple, ChatCircle, Phone, PaperPlaneTilt,
  CheckCircle, PlugsConnected, Plug, ArrowClockwise, SignOut, QrCode,
  UsersThree, Clock, Pencil, Trash, Plus, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const CH_COLOR = {
  personal_whatsapp: '#25D366',
  business_whatsapp: '#128C7E',
  whatsapp: '#25D366',
  sms: '#2B6EF0',
  email: '#8B5CF6',
  call: '#F97316',
  note: '#808080',
};
const CH_ICON = {
  personal_whatsapp: WhatsappLogo,
  business_whatsapp: WhatsappLogo,
  whatsapp: WhatsappLogo,
  sms: ChatCircle,
  email: EnvelopeSimple,
  call: Phone,
  note: ChatCircle,
};

const PROVIDER_STATUS_COLOR = { connected: '#10A882', mock: '#E5A100', disconnected: '#EF4444', qr_ready: '#2B6EF0' };
const PROVIDER_STATUS_LABEL = { connected: 'connected', mock: 'mock', disconnected: 'disconnected', qr_ready: 'awaiting scan' };

export default function CommunicationPage() {
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [showPairing, setShowPairing] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ channel: 'personal_whatsapp', to: '', student_name: '', content: '', template_id: '' });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [baileysStatus, setBaileysStatus] = useState({ connected: false, status: 'disconnected', user: null });
  const [baileysQR, setBaileysQR] = useState(null);
  const pollRef = useRef(null);

  // Bulk send state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkForm, setBulkForm] = useState({ channel: 'personal_whatsapp', template: '', filters: { stage: '', source: '', branch_id: '' } });

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  // Scheduled messages state
  const [scheduledMessages, setScheduledMessages] = useState([]);

  // Template management state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', channel: 'whatsapp', category: 'general', content: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [c, t, p, s] = await Promise.all([
        api.get('/api/communications'),
        api.get('/api/templates'),
        api.get('/api/messaging/providers'),
        api.get('/api/messaging/scheduled').catch(() => ({ data: [] })),
      ]);
      setLogs(c.data.communications || []);
      setTemplates(t.data.templates || []);
      setProviders(p.data.providers || []);
      setScheduledMessages(Array.isArray(s.data) ? s.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchBaileysStatus = useCallback(async () => {
    try {
      const [st, qr] = await Promise.all([
        api.get('/api/messaging/baileys/status'),
        api.get('/api/messaging/baileys/qr'),
      ]);
      setBaileysStatus(st.data);
      setBaileysQR(qr.data.qr || null);
      return st.data;
    } catch {
      return { connected: false, status: 'disconnected' };
    }
  }, []);

  const openPairingDialog = async () => {
    setShowPairing(true);
    // Trigger reconnect to start generating QR
    try { await api.post('/api/messaging/baileys/reconnect'); } catch {}
    await fetchBaileysStatus();
    // Start polling for status
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await fetchBaileysStatus();
      if (st.connected) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.success('WhatsApp connected!');
        fetchAll(); // Refresh providers
      }
    }, 3000);
  };

  const closePairingDialog = () => {
    setShowPairing(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/api/messaging/baileys/disconnect');
      setBaileysStatus({ connected: false, status: 'disconnected', user: null });
      setBaileysQR(null);
      toast.success('Disconnected');
      fetchAll();
    } catch { toast.error('Disconnect failed'); }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Map template channel to our new channel names
    let channel = template.channel;
    if (channel === 'whatsapp') channel = 'personal_whatsapp';
    setForm(prev => ({
      ...prev,
      channel,
      content: template.body,
      template_id: template.name,
    }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.to.trim()) { toast.error('Recipient is required'); return; }
    if (!form.content.trim()) { toast.error('Message is required'); return; }

    // If scheduling is enabled, use the schedule endpoint
    if (scheduleEnabled) {
      if (!scheduleAt) { toast.error('Select a date/time for scheduling'); return; }
      setSending(true);
      try {
        await api.post('/api/messaging/schedule', {
          channel: form.channel,
          to: form.to,
          content: form.content,
          lead_id: '',
          scheduled_at: new Date(scheduleAt).toISOString(),
        });
        toast.success('Message scheduled');
        setShowSend(false);
        setForm({ channel: 'personal_whatsapp', to: '', student_name: '', content: '', template_id: '' });
        setSelectedTemplate(null);
        setScheduleEnabled(false);
        setScheduleAt('');
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Scheduling failed');
      } finally { setSending(false); }
      return;
    }

    setSending(true);
    try {
      const res = await api.post('/api/messaging/send', {
        channel: form.channel,
        to: form.to,
        content: form.content,
        student_name: form.student_name,
        template_id: form.template_id,
      });
      if (res.data.success) {
        toast.success(`Sent via ${res.data.provider}`);
      } else {
        toast.error(res.data.error || 'Send failed — is your WhatsApp connected?');
      }
      setShowSend(false);
      setForm({ channel: 'personal_whatsapp', to: '', student_name: '', content: '', template_id: '' });
      setSelectedTemplate(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  const handleBulkSend = async () => {
    if (!bulkForm.template.trim()) { toast.error('Template message is required'); return; }
    const hasFilter = bulkForm.filters.stage || bulkForm.filters.source || bulkForm.filters.branch_id;
    if (!hasFilter) { toast.error('Select at least one filter'); return; }
    setBulkSending(true);
    try {
      const filters = {};
      if (bulkForm.filters.stage) filters.stage = bulkForm.filters.stage;
      if (bulkForm.filters.source) filters.source = bulkForm.filters.source;
      if (bulkForm.filters.branch_id) filters.branch_id = bulkForm.filters.branch_id;
      const res = await api.post('/api/messaging/bulk', {
        channel: bulkForm.channel,
        template: bulkForm.template,
        filters,
      });
      toast.success(`Queued ${res.data.sent} messages to ${res.data.total_leads} leads`);
      setShowBulk(false);
      setBulkForm({ channel: 'personal_whatsapp', template: '', filters: { stage: '', source: '', branch_id: '' } });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk send failed');
    } finally { setBulkSending(false); }
  };

  const handleCancelScheduled = async (id) => {
    try {
      await api.delete(`/api/messaging/scheduled/${id}`);
      toast.success('Scheduled message cancelled');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    try {
      if (editingTemplate) {
        await api.put(`/api/templates/${editingTemplate._id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/api/templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', channel: 'whatsapp', category: 'general', content: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`/api/templates/${id}`);
      toast.success('Template deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, channel: t.channel, category: t.category || 'general', content: t.body || t.content || '' });
    setShowTemplateForm(true);
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  const personalWA = providers.find(p => p.channel === 'personal_whatsapp');

  return (
    <div data-testid="communication-page" className="stagger-in">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Communication Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">Unified messaging with provider abstraction</p>
          {/* Provider status indicators */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {providers.map(p => (
              <span key={p.channel} className="inline-flex items-center gap-1.5 text-[10px] font-mono" data-testid={`provider-${p.channel}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PROVIDER_STATUS_COLOR[p.status] || '#808080' }} />
                <span className="text-muted-foreground">{p.name}:</span>
                <span className="font-semibold" style={{ color: PROVIDER_STATUS_COLOR[p.status] || '#808080' }}>
                  {PROVIDER_STATUS_LABEL[p.status] || p.status}
                </span>
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {/* Pair WhatsApp button */}
          <Button
            variant="outline"
            onClick={openPairingDialog}
            className="h-9 rounded-lg text-xs gap-1.5 font-semibold"
            data-testid="pair-whatsapp-btn"
          >
            {personalWA?.status === 'connected' ? (
              <><PlugsConnected size={14} weight="bold" style={{ color: '#25D366' }} /> Connected</>
            ) : (
              <><QrCode size={14} weight="bold" /> Pair WhatsApp</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowBulk(true)}
            className="h-9 rounded-lg text-xs gap-1.5 font-semibold"
            data-testid="bulk-send-btn"
          >
            <UsersThree size={14} weight="bold" /> Bulk Send
          </Button>
          <Dialog open={showSend} onOpenChange={(open) => {
            setShowSend(open);
            if (!open) { setSelectedTemplate(null); setScheduleEnabled(false); setScheduleAt(''); setForm({ channel: 'personal_whatsapp', to: '', student_name: '', content: '', template_id: '' }); }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="send-message-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
                <PaperPlaneTilt size={14} weight="bold" /> Send Message
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-xl max-w-md" data-testid="send-message-dialog">
              <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Send Message</DialogTitle></DialogHeader>
              <form onSubmit={handleSend} className="space-y-3">
                {/* Template picker */}
                {templates.length > 0 && (
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Template (optional)</Label>
                    <select
                      value={selectedTemplate?.name || ''}
                      onChange={e => {
                        const t = templates.find(t => t.name === e.target.value);
                        if (t) handleTemplateSelect(t);
                        else { setSelectedTemplate(null); setForm(prev => ({ ...prev, content: '', template_id: '' })); }
                      }}
                      className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"
                      data-testid="template-picker"
                    >
                      <option value="">No template</option>
                      {templates.map(t => (
                        <option key={t.name} value={t.name}>{t.name} ({t.channel})</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Channel</Label>
                    <select data-testid="msg-channel" value={form.channel} onChange={e => setForm({...form, channel: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                      <option value="personal_whatsapp">Personal WhatsApp</option>
                      <option value="business_whatsapp">Business WhatsApp</option>
                      <option value="sms">SMS</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">To (phone/email) *</Label>
                    <Input data-testid="msg-to" value={form.to} onChange={e => setForm({...form, to: e.target.value})} className="h-9 rounded-lg text-sm" placeholder="9820001234" required />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Student Name</Label>
                  <Input data-testid="msg-student" value={form.student_name} onChange={e => setForm({...form, student_name: e.target.value})} className="h-9 rounded-lg text-sm" placeholder="Optional" />
                </div>

                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Message *</Label>
                  <Textarea data-testid="msg-content" value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="rounded-lg text-sm min-h-[80px]" required />
                </div>

                {/* Provider info */}
                {(() => {
                  const p = providers.find(pr => pr.channel === form.channel);
                  return p ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50 text-[10px]" data-testid="provider-info">
                      <CheckCircle size={13} style={{ color: PROVIDER_STATUS_COLOR[p.status] }} />
                      <span className="text-muted-foreground">Provider:</span>
                      <span className="font-semibold text-foreground">{p.name}</span>
                      <span className="font-mono uppercase tracking-wider" style={{ color: PROVIDER_STATUS_COLOR[p.status] }}>
                        ({PROVIDER_STATUS_LABEL[p.status] || p.status})
                      </span>
                      {p.channel === 'personal_whatsapp' && p.status !== 'connected' && (
                        <button type="button" onClick={() => { setShowSend(false); openPairingDialog(); }} className="ml-auto text-[10px] font-bold" style={{ color: '#E04B2A' }}>
                          Pair first
                        </button>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Schedule toggle */}
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-muted-foreground" />
                    <Label className="text-[11px] font-semibold text-muted-foreground">Schedule for later</Label>
                  </div>
                  <Switch data-testid="schedule-toggle" checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} />
                </div>
                {scheduleEnabled && (
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Send at</Label>
                    <Input
                      type="datetime-local"
                      data-testid="schedule-datetime"
                      value={scheduleAt}
                      onChange={e => setScheduleAt(e.target.value)}
                      className="h-9 rounded-lg text-sm"
                      required
                    />
                  </div>
                )}

                <Button type="submit" data-testid="submit-message-btn" disabled={sending} className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
                  {sending ? (scheduleEnabled ? 'Scheduling...' : 'Sending...') : (scheduleEnabled ? 'Schedule Message' : 'Send Message')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="logs">
        <TabsList className="bg-secondary rounded-lg h-9">
          <TabsTrigger value="logs" data-testid="tab-comm-logs" className="text-xs rounded-md font-mono">Log ({logs.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-comm-templates" className="text-xs rounded-md font-mono">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-comm-scheduled" className="text-xs rounded-md font-mono">Scheduled ({scheduledMessages.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Channel</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Student</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Message</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Provider</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Sent By</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Date</TableHead>
            </TableRow></TableHeader><TableBody>{logs.map((l, i) => {
              const ch = l.channel || 'note';
              const Icon = CH_ICON[ch] || ChatCircle;
              const displayChannel = ch === 'personal_whatsapp' ? 'Personal WA' : ch === 'business_whatsapp' ? 'Business WA' : ch;
              return (
                <TableRow key={i} data-testid={`comm-row-${i}`}>
                  <TableCell className="py-2.5"><span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: CH_COLOR[ch] || '#808080' }}><Icon size={13} />{displayChannel}</span></TableCell>
                  <TableCell className="text-[12px] font-semibold py-2.5">{l.student_name}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground py-2.5 max-w-xs truncate">{l.content}</TableCell>
                  <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{l.provider || '-'}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground py-2.5">{l.sent_by_name}</TableCell>
                  <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</TableCell>
                </TableRow>
              );
            })}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', channel: 'whatsapp', category: 'general', content: '' }); setShowTemplateForm(true); }}
              className="h-8 rounded-lg text-xs gap-1.5 font-bold text-white"
              style={{ backgroundColor: '#E04B2A' }}
              data-testid="create-template-btn"
            >
              <Plus size={13} weight="bold" /> New Template
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((t, i) => (
              <Card key={t._id || i} data-testid={`template-card-${i}`} className="border border-border rounded-xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[12px] font-semibold text-foreground">{t.name}</span>
                    <span className="text-[9px] font-mono uppercase tracking-wider font-bold" style={{ color: CH_COLOR[t.channel] || '#808080' }}>{t.channel}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{t.body || t.content}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{t.category}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditTemplate(t)}
                        className="text-[10px] font-bold transition-colors px-2 py-0.5 rounded text-muted-foreground hover:text-foreground"
                        data-testid={`edit-template-${i}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => t._id && handleDeleteTemplate(t._id)}
                        className="text-[10px] font-bold transition-colors px-2 py-0.5 rounded text-muted-foreground hover:text-destructive"
                        data-testid={`delete-template-${i}`}
                      >
                        <Trash size={12} />
                      </button>
                      <button
                        onClick={() => { handleTemplateSelect(t); setShowSend(true); }}
                        className="text-[10px] font-bold transition-colors px-2 py-0.5 rounded"
                        style={{ color: '#E04B2A' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(224,75,42,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        data-testid={`use-template-${i}`}
                      >
                        Use
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="scheduled" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            {scheduledMessages.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">No scheduled messages</div>
            ) : (
              <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Channel</TableHead>
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">To</TableHead>
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Message</TableHead>
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Scheduled At</TableHead>
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Created By</TableHead>
                <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Actions</TableHead>
              </TableRow></TableHeader><TableBody>{scheduledMessages.map((m, i) => {
                const ch = m.channel || 'note';
                const Icon = CH_ICON[ch] || ChatCircle;
                return (
                  <TableRow key={m._id || i} data-testid={`scheduled-row-${i}`}>
                    <TableCell className="py-2.5"><span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: CH_COLOR[ch] || '#808080' }}><Icon size={13} />{ch}</span></TableCell>
                    <TableCell className="text-[12px] font-semibold py-2.5 font-mono">{m.to}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground py-2.5 max-w-xs truncate">{m.content}</TableCell>
                    <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{m.scheduled_at ? new Date(m.scheduled_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground py-2.5">{m.created_by_name}</TableCell>
                    <TableCell className="py-2.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelScheduled(m._id)}
                        className="h-7 text-[10px] font-semibold text-destructive hover:text-destructive gap-1"
                        data-testid={`cancel-scheduled-${i}`}
                      >
                        <X size={11} /> Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}</TableBody></Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* WhatsApp Pairing Dialog */}
      <Dialog open={showPairing} onOpenChange={(open) => { if (!open) closePairingDialog(); else openPairingDialog(); }}>
        <DialogContent className="rounded-xl max-w-sm" data-testid="pairing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-foreground text-lg flex items-center gap-2">
              <WhatsappLogo size={22} weight="fill" style={{ color: '#25D366' }} /> Personal WhatsApp
            </DialogTitle>
          </DialogHeader>

          {baileysStatus.connected ? (
            <div className="space-y-4 py-2">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'rgba(37,211,102,0.1)' }}>
                  <PlugsConnected size={28} weight="fill" style={{ color: '#25D366' }} />
                </div>
                <p className="font-display text-lg font-bold text-foreground">Connected</p>
                {baileysStatus.user?.name && (
                  <p className="text-sm text-muted-foreground mt-1">{baileysStatus.user.name}</p>
                )}
                {baileysStatus.user?.id && (
                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{baileysStatus.user.id}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={closePairingDialog} variant="outline" className="flex-1 h-9 rounded-lg text-xs font-semibold" data-testid="close-pairing-btn">Done</Button>
                <Button onClick={handleDisconnect} variant="outline" className="h-9 rounded-lg text-xs font-semibold gap-1 text-destructive hover:text-destructive" data-testid="disconnect-btn">
                  <SignOut size={13} /> Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <p className="text-[11px] text-muted-foreground text-center">
                Open WhatsApp on your phone &rarr; Settings &rarr; Linked Devices &rarr; Link a Device
              </p>

              {baileysQR ? (
                <div className="flex justify-center">
                  <div className="p-3 bg-white rounded-xl border border-border" data-testid="baileys-qr-code">
                    <QRCodeSVG value={baileysQR} size={200} level="H" />
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <ArrowClockwise size={24} className="animate-spin mx-auto text-muted-foreground mb-2" />
                  <p className="text-[11px] text-muted-foreground">
                    {baileysStatus.status === 'connecting' ? 'Connecting to WhatsApp...' : 'Waiting for QR code...'}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground">
                <Plug size={11} />
                <span className="font-mono uppercase tracking-wider">
                  Status: {PROVIDER_STATUS_LABEL[baileysStatus.status] || baileysStatus.status}
                </span>
              </div>

              <p className="text-[9px] text-muted-foreground text-center leading-relaxed">
                Uses Baileys (WhatsApp Web protocol). Your phone must stay connected to the internet.
                Messages are sent from your personal number.
              </p>

              <Button onClick={closePairingDialog} variant="outline" className="w-full h-9 rounded-lg text-xs font-semibold" data-testid="close-pairing-btn">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Send Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="rounded-xl max-w-md" data-testid="bulk-send-dialog">
          <DialogHeader><DialogTitle className="font-display text-foreground text-lg flex items-center gap-2"><UsersThree size={20} weight="bold" /> Bulk Send</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Channel</Label>
              <select
                data-testid="bulk-channel"
                value={bulkForm.channel}
                onChange={e => setBulkForm({ ...bulkForm, channel: e.target.value })}
                className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"
              >
                <option value="personal_whatsapp">Personal WhatsApp</option>
                <option value="business_whatsapp">Business WhatsApp</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Filter by Stage</Label>
              <Input
                data-testid="bulk-filter-stage"
                value={bulkForm.filters.stage}
                onChange={e => setBulkForm({ ...bulkForm, filters: { ...bulkForm.filters, stage: e.target.value } })}
                className="h-9 rounded-lg text-sm"
                placeholder="e.g. new_inquiry, demo_scheduled"
              />
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Filter by Source</Label>
              <Input
                data-testid="bulk-filter-source"
                value={bulkForm.filters.source}
                onChange={e => setBulkForm({ ...bulkForm, filters: { ...bulkForm.filters, source: e.target.value } })}
                className="h-9 rounded-lg text-sm"
                placeholder="e.g. website, referral"
              />
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Message Template</Label>
              <Textarea
                data-testid="bulk-template"
                value={bulkForm.template}
                onChange={e => setBulkForm({ ...bulkForm, template: e.target.value })}
                className="rounded-lg text-sm min-h-[80px]"
                placeholder="Hi {{student_name}}, ..."
              />
              <p className="text-[9px] text-muted-foreground mt-1">Variables: {'{{student_name}}'}, {'{{parent_name}}'}, {'{{stage}}'}</p>
            </div>

            <Button
              onClick={handleBulkSend}
              disabled={bulkSending}
              data-testid="submit-bulk-btn"
              className="w-full h-9 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: '#E04B2A' }}
            >
              {bulkSending ? 'Sending...' : 'Send to Matching Leads'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Create/Edit Dialog */}
      <Dialog open={showTemplateForm} onOpenChange={(open) => { setShowTemplateForm(open); if (!open) { setEditingTemplate(null); } }}>
        <DialogContent className="rounded-xl max-w-md" data-testid="template-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-foreground text-lg">{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label>
              <Input
                data-testid="tpl-name"
                value={templateForm.name}
                onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                className="h-9 rounded-lg text-sm"
                placeholder="e.g. Welcome Message"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Channel</Label>
                <select
                  data-testid="tpl-channel"
                  value={templateForm.channel}
                  onChange={e => setTemplateForm({ ...templateForm, channel: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Category</Label>
                <Input
                  data-testid="tpl-category"
                  value={templateForm.category}
                  onChange={e => setTemplateForm({ ...templateForm, category: e.target.value })}
                  className="h-9 rounded-lg text-sm"
                  placeholder="e.g. inquiry, payment"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Content *</Label>
              <Textarea
                data-testid="tpl-content"
                value={templateForm.content}
                onChange={e => setTemplateForm({ ...templateForm, content: e.target.value })}
                className="rounded-lg text-sm min-h-[100px]"
                placeholder="Hi {{student_name}}, ..."
              />
            </div>
            <Button
              onClick={handleSaveTemplate}
              data-testid="submit-template-btn"
              className="w-full h-9 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: '#E04B2A' }}
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
